// 시스템 설정 패널 — 서버 저장 설정(검사/업로드 · 격리/로그 · Webhook · 관리자 보안) + IP 접근제어(모드·허용 목록)
import { useEffect, useState } from 'react';
import { useUI } from '../lib/uiStore';
import { audit, applyAuthPolicy } from '../lib/auth';
import { adminGet, adminPost, adminPut, adminDelete, AdminApiError } from '../lib/adminApi';
import {
  DEFAULT_SYSTEM_SETTINGS,
  type SystemSettings,
  type SettingsForm,
  type IpAccessMode,
  type IpAllowItem,
  type SettingsResponse,
  type IpAccessResponse,
} from '../lib/settingsTypes';
import { ToggleRow } from '../components/ToggleRow';

type NumberKey = { [K in keyof SettingsForm]: SettingsForm[K] extends number ? K : never }[keyof SettingsForm];
type BoolKey = { [K in keyof SettingsForm]: SettingsForm[K] extends boolean ? K : never }[keyof SettingsForm];

interface NumberField { key: NumberKey; label: string; min: number; max: number }
interface BoolField { key: BoolKey; label: string }

// 범위는 packages/shared/src/settings.ts 의 zod 스키마와 동일 (input min/max 힌트용 — 최종 검증은 서버)
const SCAN_NUMBERS: NumberField[] = [
  { key: 'maxUploadMb', label: '최대 업로드 크기 (MB)', min: 1, max: 100 },
  { key: 'rateLimitPerMinute', label: '분당 요청 한도 (클라이언트별)', min: 1, max: 100_000 },
  { key: 'rateLimitWindowSeconds', label: '요청 한도 윈도우 (초)', min: 1, max: 3600 },
];
const SCAN_TOGGLES: BoolField[] = [
  { key: 'engineClamavEnabled', label: 'ClamAV 검사' },
  { key: 'engineYaraEnabled', label: 'YARA 룰 검사' },
  { key: 'engineHashBlacklistEnabled', label: '해시 블랙리스트 검사' },
  { key: 'magicBytesCheckEnabled', label: '파일 헤더 검증 (Magic Bytes)' },
];
const RETENTION_NUMBERS: NumberField[] = [
  { key: 'quarantineRetentionDays', label: '격리 보관 기간 (일)', min: 1, max: 3650 },
  { key: 'scanLogRetentionDays', label: '검사 로그 보관 기간 (일)', min: 1, max: 3650 },
];
const WEBHOOK_NUMBERS: NumberField[] = [
  { key: 'webhookMaxAttempts', label: '최대 전송 시도 횟수 (최초 전송 포함)', min: 1, max: 20 },
  { key: 'webhookRetryDelaySeconds', label: '재시도 기본 간격 (초, exponential backoff)', min: 1, max: 86_400 },
  { key: 'webhookTimeoutMs', label: '전송 타임아웃 (ms)', min: 1000, max: 120_000 },
];
const ADMIN_NUMBERS: NumberField[] = [
  { key: 'adminMaxLoginAttempts', label: '로그인 실패 허용 횟수', min: 1, max: 20 },
  { key: 'adminLockoutMinutes', label: '락아웃 시간 (분)', min: 1, max: 1440 },
  { key: 'adminIdleTimeoutMinutes', label: '무활동 자동 로그아웃 (분)', min: 1, max: 1440 },
];

function toForm(s: SystemSettings): SettingsForm {
  const { ipAccessMode: _mode, ...form } = s;
  return form;
}

// 저장/복원된 관리자 보안 값을 로그인 정책(localStorage)에 즉시 반영 — 새로고침 없이 다음 로그인 화면부터 적용
function applyAdminAuthPolicy(s: SystemSettings) {
  applyAuthPolicy({
    maxAttempts: s.adminMaxLoginAttempts,
    lockoutMs: s.adminLockoutMinutes * 60_000,
    idleTimeoutMs: s.adminIdleTimeoutMinutes * 60_000,
  });
}

// 서버가 준 message(400/409 잠금 방지 등)는 그대로, 네트워크 오류는 fallback 문구
function errorMessage(e: unknown, fallback: string): string {
  return e instanceof AdminApiError ? e.message : fallback;
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div className="text-muted mt-12" style={{ lineHeight: 1.7 }}>{children}</div>;
}

export function Settings() {
  const { showToast, openModal } = useUI();
  const [form, setForm] = useState<SettingsForm>(() => toForm(DEFAULT_SYSTEM_SETTINGS));
  const [saved, setSaved] = useState<SettingsForm>(() => toForm(DEFAULT_SYSTEM_SETTINGS));
  const [mode, setMode] = useState<IpAccessMode>('allow_all');
  const [clientIp, setClientIp] = useState('');
  const [items, setItems] = useState<IpAllowItem[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ipCidr, setIpCidr] = useState('');
  const [ipNote, setIpNote] = useState('');

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  function applySettings(s: SystemSettings) {
    const f = toForm(s);
    setForm(f);
    setSaved(f);
    setMode(s.ipAccessMode);
  }

  async function loadIpAccess() {
    const d = await adminGet<IpAccessResponse>('/admin/ip-access');
    setMode(d.mode);
    setClientIp(d.clientIp);
    setItems(d.items);
  }

  useEffect(() => {
    let alive = true;
    Promise.all([adminGet<SettingsResponse>('/admin/settings'), loadIpAccess()])
      .then(([s]) => {
        if (!alive) return;
        applySettings(s.settings);
        setClientIp(s.clientIp);
        setLoadFailed(false);
      })
      .catch(() => {
        if (!alive) return;
        setLoadFailed(true);
        showToast('설정 로드 실패 — 백엔드 연결을 확인하세요', 'error');
      });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function setField<K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSave() {
    setBusy(true);
    try {
      // 마지막 저장값과 다른 키만 전송 — 건드리지 않은 env 기본값 키가 DB에 고정되지 않도록
      const patch = Object.fromEntries(
        (Object.keys(form) as (keyof SettingsForm)[]).filter((k) => form[k] !== saved[k]).map((k) => [k, form[k]]),
      );
      const res = await adminPut<{ settings: SystemSettings }>('/admin/settings', patch);
      applySettings(res.settings);
      applyAdminAuthPolicy(res.settings);
      audit('admin.action', 'settings.save');
      showToast('설정이 저장되었습니다', 'success');
    } catch (e) {
      showToast(errorMessage(e, '설정 저장 실패'), 'error');
    } finally {
      setBusy(false);
    }
  }

  function onReset() {
    openModal({
      title: '⚠ 기본값 복원',
      sub: '모든 시스템 설정이 <strong>기본값으로 되돌아갑니다</strong>.',
      body: '<div>IP 접근제어도 전체 허용으로 바뀝니다. 허용 목록은 유지됩니다.</div>',
      confirmLabel: '확인',
      onConfirm: () => {
        void (async () => {
          try {
            const res = await adminPost<{ settings: SystemSettings }>('/admin/settings/reset', {});
            applySettings(res.settings);
            applyAdminAuthPolicy(res.settings);
            audit('admin.action', 'settings.reset');
            showToast('기본값으로 복원되었습니다', 'success');
          } catch (e) {
            showToast(errorMessage(e, '기본값 복원 실패'), 'error');
          }
        })();
      },
    });
  }

  async function putMode(next: IpAccessMode) {
    try {
      const res = await adminPut<{ settings: SystemSettings }>('/admin/settings', { ipAccessMode: next });
      setMode(res.settings.ipAccessMode);
      audit('admin.action', `ip.mode=${next}`);
      showToast(next === 'deny_all' ? '차단 모드가 켜졌습니다' : '전체 허용으로 전환되었습니다', 'success');
    } catch (e) {
      showToast(errorMessage(e, 'IP 접근 모드 변경 실패'), 'error');
    }
  }

  function onToggleMode() {
    if (mode === 'deny_all') { void putMode('allow_all'); return; }
    openModal({
      title: '🌐 차단 모드 전환',
      sub: '허용 목록의 IP만 접근을 허용합니다.',
      body: `<div>허용 목록에 없는 IP는 API(/v1)와 관리자 API(/admin/*) 모두 403 으로 차단됩니다.</div><div>관리자 화면 자체는 열리지만 모든 데이터 호출이 403 이 됩니다.</div><div>현재 접속 IP: <strong>${clientIp}</strong>. 서버 자신(127.0.0.1)은 항상 허용됩니다.</div>`,
      confirmLabel: '확인',
      onConfirm: () => { void putMode('deny_all'); },
    });
  }

  async function addCidr(cidr: string, note: string) {
    try {
      await adminPost('/admin/ip-access', { cidr, note });
      audit('admin.action', `ip.add cidr=${cidr}`);
      showToast(`${cidr} 이(가) 허용 목록에 추가되었습니다`, 'success');
      setIpCidr('');
      setIpNote('');
      await loadIpAccess();
    } catch (e) {
      showToast(errorMessage(e, 'IP 추가 실패'), 'error');
    }
  }

  function onDelete(item: IpAllowItem) {
    openModal({
      title: '🗑 허용 IP 삭제',
      sub: `<strong>${item.cidr}</strong> 을(를) 허용 목록에서 삭제합니다.`,
      body: '<div>차단 모드에서는 이 IP의 접근이 즉시 차단됩니다.</div>',
      confirmLabel: '확인',
      onConfirm: () => {
        void (async () => {
          try {
            await adminDelete(`/admin/ip-access/${item.id}`);
            audit('admin.action', `ip.delete cidr=${item.cidr}`);
            showToast(`${item.cidr} 이(가) 삭제되었습니다`, 'success');
            await loadIpAccess();
          } catch (e) {
            showToast(errorMessage(e, 'IP 삭제 실패'), 'error');
          }
        })();
      },
    });
  }

  const myIpListed = Boolean(clientIp) && items.some((i) => i.cidr === clientIp);
  const cidrTrimmed = ipCidr.trim();

  const renderNumbers = (fields: NumberField[]) => fields.map((f) => (
    <div className="form-row" key={f.key}>
      <label className="form-label" htmlFor={`set-${f.key}`}>{f.label}</label>
      <input
        id={`set-${f.key}`}
        className="form-input"
        type="number"
        min={f.min}
        max={f.max}
        value={form[f.key]}
        onChange={(e) => setField(f.key, Number(e.target.value) || 0)}
      />
    </div>
  ));

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">
          🛠 시스템 설정 <em>서버 저장 · 즉시 반영</em>
          {dirty && (
            <span
              style={{
                marginLeft: 10,
                fontSize: 12,
                color: 'var(--yellow)',
                background: 'var(--yellow-dim)',
                padding: '2px 8px',
                borderRadius: 4,
                fontFamily: 'JetBrains Mono, monospace',
              }}
            >
              변경됨 · 저장 필요
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onReset} disabled={loadFailed || busy}>기본값 복원</button>
          <button
            className="btn btn-pri btn-sm"
            onClick={() => void onSave()}
            disabled={!dirty || loadFailed || busy}
            style={{ opacity: !dirty || loadFailed || busy ? 0.4 : 1 }}
          >
            💾 저장
          </button>
        </div>
      </div>

      <div className="settings">
        <div className="set-card">
          <div className="set-title">🦠 검사 / 업로드</div>
          {renderNumbers(SCAN_NUMBERS)}
          {SCAN_TOGGLES.map((t) => (
            <ToggleRow key={t.key} label={t.label} on={form[t.key]} onClick={() => setField(t.key, !form[t.key])} />
          ))}
          <Hint>엔진 토글은 모든 정책에 우선하는 전역 스위치입니다. 모두 끄면 검사 결과는 failed 로 처리됩니다.</Hint>
        </div>

        <div className="set-card">
          <div className="set-title">🔒 격리 / 로그 보관</div>
          {renderNumbers(RETENTION_NUMBERS)}
          <Hint>워커가 1시간마다 만료된 격리 파일과 오래된 검사 로그를 정리합니다.</Hint>
        </div>

        <div className="set-card">
          <div className="set-title">🔔 Webhook</div>
          {renderNumbers(WEBHOOK_NUMBERS)}
        </div>

        <div className="set-card">
          <div className="set-title">🔐 관리자 보안</div>
          {renderNumbers(ADMIN_NUMBERS)}
          <Hint>저장 후 다음 로그인 화면부터 적용됩니다.</Hint>
        </div>

        <div className="set-card" style={{ gridColumn: '1 / -1' }}>
          <div className="set-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            🌐 IP 접근제어
            <span className={`badge-pill ${mode === 'deny_all' ? 'bp-quarantine' : 'bp-safe'}`}>
              {mode === 'deny_all' ? `현재: 차단 모드 — 허용 목록 ${items.length}개` : '현재: 전체 허용'}
            </span>
          </div>
          <ToggleRow label="허용 목록의 IP만 접근 허용 (차단 모드)" on={mode === 'deny_all'} onClick={onToggleMode} />

          <div className="form-row mt-12" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="text-muted">현재 접속 IP:</span>
            <span className="text-mono">{clientIp || '—'}</span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => void addCidr(clientIp, '관리자 (내 IP 추가)')}
              disabled={!clientIp || myIpListed}
            >
              ➕ 내 IP 허용 목록에 추가
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
            <div className="form-row" style={{ flex: 2, minWidth: 220, marginBottom: 0 }}>
              <label className="form-label" htmlFor="ip-cidr">IP 또는 CIDR</label>
              <input
                id="ip-cidr"
                className="form-input mono"
                placeholder="203.0.113.7 또는 10.0.0.0/8"
                value={ipCidr}
                onChange={(e) => setIpCidr(e.target.value)}
              />
            </div>
            <div className="form-row" style={{ flex: 2, minWidth: 180, marginBottom: 0 }}>
              <label className="form-label" htmlFor="ip-note">메모 (선택)</label>
              <input
                id="ip-note"
                className="form-input"
                placeholder="메모 (선택)"
                value={ipNote}
                onChange={(e) => setIpNote(e.target.value)}
                maxLength={200}
              />
            </div>
            <button
              type="button"
              className="btn btn-pri"
              onClick={() => void addCidr(cidrTrimmed, ipNote.trim())}
              disabled={!cidrTrimmed}
              style={{ opacity: !cidrTrimmed ? 0.4 : 1 }}
            >
              추가
            </button>
          </div>

          {items.length === 0 ? (
            <div className="card" style={{ padding: 18, textAlign: 'center', color: 'var(--text3)' }}>
              등록된 허용 IP가 없습니다.
            </div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>IP/CIDR</th>
                    <th>메모</th>
                    <th>등록일</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td className="text-mono">{it.cidr}</td>
                      <td>{it.note ?? <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                      <td className="text-mono text-muted">{new Date(it.createdAt).toLocaleString('ko-KR')}</td>
                      <td>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(it)}>삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Hint>차단 모드에서도 서버 자신(127.0.0.1)은 항상 허용되어 잠금 시 서버에서 curl 로 복구할 수 있습니다.</Hint>
        </div>
      </div>
    </>
  );
}
